/**
 * 书签与书签文件夹服务（Supabase）
 * 表：bookmark_folders, bookmarks
 */

import { supabase } from '@ext-bookmark/shared/api';
import type { BookmarkFolder, Bookmark } from '@ext-bookmark/shared/types';

type RowFolder = {
  id: string;
  user_id: string;
  name: string;
  parent_id: string | null;
  position: number;
  created_at: string;
  updated_at: string;
};

type RowBookmark = {
  id: string;
  user_id: string;
  folder_id: string;
  name: string;
  url: string;
  note: string | null;
  position: number;
  created_at: string;
  updated_at: string;
};

function toFolder(row: RowFolder): BookmarkFolder {
  return {
    id: row.id,
    user_id: row.user_id,
    name: row.name,
    parent_id: row.parent_id,
    position: row.position,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function toBookmark(row: RowBookmark): Bookmark {
  return {
    id: row.id,
    name: row.name,
    url: row.url,
    note: row.note ?? undefined,
    position: row.position,
    folderId: row.folder_id,
  };
}

export async function listBookmarkFolders(userId: string): Promise<{ folders: BookmarkFolder[]; error: Error | null }> {
  if (!supabase) {
    return { folders: [], error: new Error('Supabase 未配置') };
  }
  const { data, error } = await supabase
    .from('bookmark_folders')
    .select('*')
    .eq('user_id', userId)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) return { folders: [], error };
  const folders = (data || []).map((r) => toFolder(r as RowFolder));
  return { folders, error: null };
}

export async function createBookmarkFolder(
  userId: string,
  params: { name: string; parentId?: string | null; position?: number }
): Promise<{ folder: BookmarkFolder | null; error: Error | null }> {
  if (!supabase) return { folder: null, error: new Error('Supabase 未配置') };
  const position = params.position ?? 0;
  const { data, error } = await supabase
    .from('bookmark_folders')
    .insert({
      user_id: userId,
      name: params.name.trim(),
      parent_id: params.parentId ?? null,
      position,
    })
    .select()
    .single();
  if (error) return { folder: null, error };
  return { folder: toFolder(data as RowFolder), error: null };
}

export async function updateBookmarkFolder(
  folderId: string,
  params: { name?: string; parentId?: string | null; position?: number }
): Promise<{ folder: BookmarkFolder | null; error: Error | null }> {
  if (!supabase) return { folder: null, error: new Error('Supabase 未配置') };
  const body: Partial<RowFolder> = {};
  if (params.name !== undefined) body.name = params.name.trim();
  if (params.parentId !== undefined) body.parent_id = params.parentId;
  if (params.position !== undefined) body.position = params.position;
  const { data, error } = await supabase
    .from('bookmark_folders')
    .update(body)
    .eq('id', folderId)
    .select()
    .single();
  if (error) return { folder: null, error };
  return { folder: toFolder(data as RowFolder), error: null };
}

export async function deleteBookmarkFolder(folderId: string): Promise<Error | null> {
  if (!supabase) return new Error('Supabase 未配置');
  const { error } = await supabase.from('bookmark_folders').delete().eq('id', folderId);
  return error ?? null;
}

export async function listBookmarks(
  userId: string,
  folderId: string | null
): Promise<{ bookmarks: Bookmark[]; error: Error | null }> {
  if (!supabase) return { bookmarks: [], error: new Error('Supabase 未配置') };
  let q = supabase.from('bookmarks').select('*').eq('user_id', userId);
  if (folderId != null) q = q.eq('folder_id', folderId);
  const { data, error } = await q.order('position', { ascending: true }).order('created_at', { ascending: true });
  if (error) return { bookmarks: [], error };
  const bookmarks = (data || []).map((r) => toBookmark(r as RowBookmark));
  return { bookmarks, error: null };
}

/** 同文件夹内是否已存在相同 name+url（排除某条时传 excludeId） */
export async function checkBookmarkDuplicate(
  folderId: string,
  name: string,
  url: string,
  excludeId?: string
): Promise<{ duplicate: boolean; error: Error | null }> {
  if (!supabase) return { duplicate: false, error: new Error('Supabase 未配置') };
  let q = supabase
    .from('bookmarks')
    .select('id')
    .eq('folder_id', folderId)
    .eq('name', name.trim())
    .eq('url', url.trim());
  if (excludeId) q = q.neq('id', excludeId);
  const { data, error } = await q.limit(1);
  if (error) return { duplicate: false, error };
  return { duplicate: (data?.length ?? 0) > 0, error: null };
}

export async function createBookmark(
  userId: string,
  folderId: string,
  params: { name: string; url: string; note?: string; position?: number }
): Promise<{ bookmark: Bookmark | null; error: Error | null }> {
  if (!supabase) return { bookmark: null, error: new Error('Supabase 未配置') };
  const position = params.position ?? 0;
  const { data, error } = await supabase
    .from('bookmarks')
    .insert({
      user_id: userId,
      folder_id: folderId,
      name: params.name.trim(),
      url: params.url.trim(),
      note: params.note?.trim() || null,
      position,
    })
    .select()
    .single();
  if (error) return { bookmark: null, error };
  return { bookmark: toBookmark(data as RowBookmark), error: null };
}

export async function updateBookmark(
  bookmarkId: string,
  params: { name?: string; url?: string; note?: string; position?: number; folderId?: string }
): Promise<{ bookmark: Bookmark | null; error: Error | null }> {
  if (!supabase) return { bookmark: null, error: new Error('Supabase 未配置') };
  const body: Partial<RowBookmark> = {};
  if (params.name !== undefined) body.name = params.name.trim();
  if (params.url !== undefined) body.url = params.url.trim();
  if (params.note !== undefined) body.note = params.note?.trim() || null;
  if (params.position !== undefined) body.position = params.position;
  if (params.folderId !== undefined) body.folder_id = params.folderId;
  const { data, error } = await supabase
    .from('bookmarks')
    .update(body)
    .eq('id', bookmarkId)
    .select()
    .single();
  if (error) return { bookmark: null, error };
  return { bookmark: toBookmark(data as RowBookmark), error: null };
}

export async function deleteBookmark(bookmarkId: string): Promise<Error | null> {
  if (!supabase) return new Error('Supabase 未配置');
  const { error } = await supabase.from('bookmarks').delete().eq('id', bookmarkId);
  return error ?? null;
}
